import { supabase } from '@/integrations/supabase/client';
import { PageContext } from '@/types/pageContext';
import { SmartChatMessage, ContextInjection } from '@/types/smartChat';
import { pageAnalysisService } from './pageAnalysisService';

class SmartChatService {
  async sendContextualMessage(
    userMessage: string,
    pageContext: PageContext | null,
    conversationHistory: SmartChatMessage[] = []
  ): Promise<{ content: string; suggestedQuestions: string[] }> {
    try {
      const contextInjection = this.buildContextInjection(
        userMessage,
        pageContext,
        conversationHistory
      );

      const { data, error } = await supabase.functions.invoke('openai-chat', {
        body: {
          messages: [
            {
              role: 'system',
              content: this.buildSystemPrompt(pageContext)
            },
            ...this.formatConversationHistory(conversationHistory),
            {
              role: 'user',
              content: contextInjection.userMessage
            }
          ],
          stream: false,
          page_context: pageContext
        }
      });

      if (error) {
        console.error('SmartChat API error:', error);
        throw new Error('Falha na comunicação com a IA');
      }

      return {
        content: data.content || 'Desculpe, não consegui processar sua mensagem.',
        suggestedQuestions: data.suggested_questions || []
      };
    } catch (error) {
      console.error('SmartChat service error:', error);
      return {
        content: 'Desculpe, ocorreu um erro. Tente novamente em alguns momentos.',
        suggestedQuestions: []
      };
    }
  }

  private buildSystemPrompt(pageContext: PageContext | null): string {
    const basePrompt = `Você é um assistente IA integrado à plataforma Justiçai, especializado em direito e tecnologia jurídica.`;

    const urlRule = `\n\nREGRA CRÍTICA DE URLs:\n- O domínio ÚNICO e OFICIAL da plataforma é: https://justicai.laib.com.br\n- NUNCA invente, sugira ou mencione outras URLs (ex.: app.justicai.com, justicai.com, justicai.laib.chat NÃO EXISTEM).\n- Ao indicar navegação, use SEMPRE caminhos relativos (ex.: /casos, /chat, /documentos) ou a URL completa começando com https://justicai.laib.com.br.\n- Se não tiver certeza da rota exata, oriente o usuário a clicar no menu lateral em vez de inventar um link.`;

    if (!pageContext) {
      return `${basePrompt}${urlRule}\n\nINSTRUÇÕES GERAIS:\n- Responda de forma útil e profissional sobre qualquer assunto\n- Mantenha respostas concisas mas informativas\n- Foque em ajudar o usuário com suas necessidades\n- Se perguntarem sobre funcionalidades da plataforma, oriente sobre navegação`;
    }

    const pageDescription = pageAnalysisService.getPageDescription(pageContext);

    return `${basePrompt}${urlRule}\n\nCONTEXTO DA PÁGINA ATUAL:\n- URL: ${pageContext.url}\n- Tipo: ${pageContext.pageType}\n- Título: ${pageContext.title}\n- Descrição: ${pageDescription}\n- Funcionalidades: ${pageContext.features.join(', ') || 'Nenhuma detectada'}\n- Formulários: ${pageContext.forms.length} formulário(s) encontrado(s)\n- Botões/Links: ${pageContext.ctas.length} ação(ões) disponível(eis)\n\nCONTEÚDO PRINCIPAL DA PÁGINA:\n${pageContext.content.substring(0, 1000)}${pageContext.content.length > 1000 ? '...' : ''}\n\nINSTRUÇÕES ESPECÍFICAS:\n1. Responda sobre qualquer assunto que o usuário perguntar\n2. Dê prioridade máxima para ajudar com o conteúdo da página atual\n3. Se perguntarem sobre a página, use o contexto fornecido acima\n4. Sugira ações relevantes baseadas na página atual quando apropriado\n5. Se houver formulários na página, ofereça ajuda para preenchê-los\n6. Se houver erros ou problemas visíveis, ofereça soluções\n7. Mantenha respostas concisas mas completas\n8. Use linguagem técnica jurídica quando apropriado\n\nFOCO: Seja extremamente útil para navegação e uso da página atual, mas responda qualquer pergunta do usuário.`;
  }

  private buildContextInjection(
    userMessage: string,
    pageContext: PageContext | null,
    conversationHistory: SmartChatMessage[]
  ): ContextInjection {
    let enhancedMessage = userMessage;

    // Add implicit context if the message is very short or unclear
    if (pageContext && userMessage.length < 50 && !userMessage.includes('?')) {
      enhancedMessage = `${userMessage} (Contexto: estou na página ${pageContext.title} - ${pageContext.url})`;
    }

    return {
      pageContext: pageContext!,
      userMessage: enhancedMessage,
      conversationHistory
    };
  }

  private formatConversationHistory(history: SmartChatMessage[]): Array<{role: 'user' | 'assistant'; content: string}> {
    // Keep only last 6 messages for API efficiency
    const recentHistory = history.slice(-6);
    
    return recentHistory.map(msg => ({
      role: msg.role,
      content: msg.content
    }));
  }

  // Fallback responses when API fails
  getFallbackResponse(pageContext: PageContext | null): string {
    if (!pageContext) {
      return "Estou temporariamente indisponível. Por favor, tente novamente em alguns momentos.";
    }

    const fallbacks: Record<string, string> = {
      dashboard: "Vejo que você está no painel principal. Posso ajudar a explicar as métricas ou orientar sobre as funcionalidades disponíveis assim que o serviço for restabelecido.",
      chat: "Você está na área de chat da plataforma. Quando o serviço for restabelecido, poderei ajudar com suas perguntas sobre direito e uso da plataforma.",
      documents: "Estou vendo que você está no editor de documentos. Posso ajudar com formatação, templates e revisão quando voltar ao normal.",
      calculators: "Vejo que está usando as calculadoras jurídicas. Quando restabelecido, posso explicar cálculos e validar dados inseridos.",
      settings: "Você está nas configurações. Posso orientar sobre as melhores configurações quando o serviço voltar.",
      default: "Serviço temporariamente indisponível. Tente novamente em alguns momentos."
    };

    return fallbacks[pageContext.pageType] || fallbacks.default;
  }
}

export const smartChatService = new SmartChatService();
